import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Wallet, Calendar, ShieldCheck, Users, Star, AirplaneTilt, CurrencyDollar, ChartLineUp, Globe, CheckCircle } from '@phosphor-icons/react';
import DropioLogo from '@/components/DropioLogo';

const BecomeATraveler = () => {
  const navigate = useNavigate();
  const [route, setRoute] = useState({ from: 'Melbourne', to: 'Dhaka', kg: 15, pricePerKg: 12 });

  const estimatedEarnings = useMemo(() => {
    const gross = route.kg * route.pricePerKg;
    const commission = gross * 0.08;
    const bookingFee = 2;
    const net = gross - commission - bookingFee;
    return { gross, commission, bookingFee, net };
  }, [route]);

  const handleStart = () => navigate('/signup');

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <button onClick={() => navigate('/')} data-testid="nav-logo"><DropioLogo size={36} /></button>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="px-5 py-2.5 text-sm font-semibold text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="nav-login">Log In</button>
            <button onClick={handleStart} className="px-5 py-2.5 text-sm font-semibold text-white dropio-gradient-cta rounded-lg shadow-lg shadow-purple-200" data-testid="nav-signup">Sign Up</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-orange-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full mb-4">EARN AS YOU TRAVEL</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="bt-hero-headline">
              Turn every journey <br />into <span className="dropio-gradient-text">opportunity</span>.
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Use your free luggage space to earn extra money on your trips. Help people send parcels while you travel — safe, simple, and rewarding.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={handleStart} className="dropio-gradient-cta text-white font-semibold px-7 py-3.5 rounded-lg shadow-lg shadow-purple-200 hover:shadow-xl transition-all flex items-center gap-2" data-testid="bt-start-button">
                Start Earning <ArrowRight size={18} weight="bold" />
              </button>
              <button onClick={() => document.getElementById('estimator')?.scrollIntoView({ behavior: 'smooth' })} className="bg-white text-gray-900 border border-gray-200 font-semibold px-7 py-3.5 rounded-lg hover:bg-gray-50">
                Calculate Earnings
              </button>
            </div>

            <div className="flex items-center gap-6 mt-8 text-sm text-gray-600">
              <div className="flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-green-500" /> No upfront cost</div>
              <div className="flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-green-500" /> Earn $50–$500/trip</div>
              <div className="flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-green-500" /> Verified senders only</div>
            </div>
          </div>

          {/* Hero illustration */}
          <div className="relative">
            <div className="absolute inset-0 dropio-gradient rounded-3xl opacity-10 blur-3xl"></div>
            <img
              src="https://images.pexels.com/photos/2087391/pexels-photo-2087391.jpeg?auto=compress&cs=tinysrgb&w=800"
              alt="Traveler with luggage"
              className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full dropio-gradient flex items-center justify-center">
                <Wallet size={24} weight="duotone" className="text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-semibold">AVG EARNINGS</p>
                <p className="text-2xl font-bold text-gray-900">$180<span className="text-sm text-gray-500">/trip</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Earnings Estimator */}
      <section id="estimator" className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-purple-100 text-[#6D28D9] text-xs font-bold rounded-full mb-3">CALCULATOR</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="estimator-heading">
              Estimate your earnings
            </h2>
            <p className="text-gray-600">See how much you could earn on your next trip</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 md:p-10 border border-purple-100">
            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Inputs */}
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">From</label>
                    <input type="text" value={route.from} onChange={(e) => setRoute({ ...route, from: e.target.value })}
                      className="w-full mt-1 px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium focus:outline-none focus:border-[#6D28D9]"
                      data-testid="estimator-from" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">To</label>
                    <input type="text" value={route.to} onChange={(e) => setRoute({ ...route, to: e.target.value })}
                      className="w-full mt-1 px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium focus:outline-none focus:border-[#6D28D9]"
                      data-testid="estimator-to" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Available capacity</label>
                    <span className="text-sm font-bold text-[#6D28D9]">{route.kg} kg</span>
                  </div>
                  <input type="range" min="1" max="40" value={route.kg} onChange={(e) => setRoute({ ...route, kg: parseInt(e.target.value) })}
                    className="w-full accent-[#6D28D9]" data-testid="estimator-kg" />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Price per kg</label>
                    <span className="text-sm font-bold text-[#6D28D9]">${route.pricePerKg}/kg</span>
                  </div>
                  <input type="range" min="5" max="30" value={route.pricePerKg} onChange={(e) => setRoute({ ...route, pricePerKg: parseInt(e.target.value) })}
                    className="w-full accent-[#6D28D9]" data-testid="estimator-price" />
                  <p className="text-xs text-gray-500 mt-1">💡 Market rate: $10–$14/kg for this route</p>
                </div>
              </div>

              {/* Results */}
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">Estimated take-home</p>
                <p className="text-5xl font-extrabold dropio-gradient-text mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="estimator-net">
                  ${estimatedEarnings.net.toFixed(0)}
                </p>
                <p className="text-sm text-gray-500 mb-5">per single trip</p>

                <div className="space-y-2 text-sm border-t border-gray-100 pt-4">
                  <Row label="Gross earnings" value={`$${estimatedEarnings.gross.toFixed(2)}`} />
                  <Row label="Platform fee (8%)" value={`–$${estimatedEarnings.commission.toFixed(2)}`} muted />
                  <Row label="Booking fee" value={`–$${estimatedEarnings.bookingFee.toFixed(2)}`} muted />
                </div>

                <button onClick={handleStart} className="w-full mt-6 dropio-gradient-cta text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2" data-testid="estimator-cta">
                  Start Earning Now <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Why travel with Dropio?</h2>
            <p className="text-gray-600">Built for modern travelers who want flexibility, security, and real earnings.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { icon: Wallet, title: 'Earn extra income', desc: 'Average travelers make $150–$300 per trip in passive income.', color: '#6D28D9' },
              { icon: Calendar, title: 'On your schedule', desc: 'You choose which parcels to carry — no obligations.', color: '#EC4899' },
              { icon: ShieldCheck, title: 'Fully protected', desc: 'Every parcel is verified and insured up to $500.', color: '#F97316' },
              { icon: Star, title: 'Build reputation', desc: 'Earn ratings, badges, and unlock higher-paying routes.', color: '#10B981' }
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg transition-shadow" data-testid={`benefit-${i}`}>
                  <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center" style={{ backgroundColor: `${b.color}15` }}>
                    <Icon size={24} weight="duotone" style={{ color: b.color }} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>How it works for travelers</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { num: '01', icon: AirplaneTilt, title: 'Post your trip', desc: 'Share your route, dates, and available kg.' },
              { num: '02', icon: Users, title: 'Accept requests', desc: 'Review parcel requests and approve the ones you like.' },
              { num: '03', icon: ShieldCheck, title: 'Carry & deliver', desc: 'Pick up the parcel and hand it over at the destination.' },
              { num: '04', icon: CurrencyDollar, title: 'Get paid', desc: 'Receive payment instantly after delivery confirmation.' }
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.num} className="relative">
                  <p className="text-5xl font-extrabold text-gray-100 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{s.num}</p>
                  <Icon size={28} weight="duotone" className="text-[#6D28D9] mb-3" />
                  <h3 className="font-bold text-gray-900 mb-1.5">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="dropio-gradient-banner rounded-3xl p-12 text-center text-white">
            <Globe size={48} weight="duotone" className="mx-auto mb-5 opacity-90" />
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Ready to monetize your next trip?</h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">Join 15,000+ travelers already earning with Dropio. Sign up takes less than 2 minutes.</p>
            <button onClick={handleStart} className="bg-white text-gray-900 font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2" data-testid="bt-final-cta">
              Become a Traveler <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

const Row = ({ label, value, muted }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-600">{label}</span>
    <span className={`font-semibold ${muted ? 'text-gray-500' : 'text-gray-900'}`}>{value}</span>
  </div>
);

export default BecomeATraveler;
